import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";
const apiUrl = process.env.REACT_APP_API_URL;
const initialState = {
  items: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 1,
  },
  lastParams: null,
  query: { search: "", limit: 12, page: 1 },
  status: "idle",
  error: null,
  success: null,
  loading: false,
};

export const getIngrediants = createAsyncThunk(
  "ingrediant/getIngrediants",
  async (restaurantId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/ingrediant`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const getIngrediantsPaginated = createAsyncThunk(
  "ingrediant/getIngrediantsPaginated",
  async ({ restaurantId, search, limit, page }) => {
    try {
      const response = await axios.get(`${apiUrl}/ingrediant`, {
        ...getAuthHeaders(false, restaurantId),
        params: {
          search,
          limit,
          page,
        },
      });
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const addIngrediant = createAsyncThunk(
  "ingrediant/addIngrediant",
  async ({ body, restaurantId }) => {
    try {
      const response = await axios.post(
        `${apiUrl}/ingrediant`,
        body,
        getAuthHeaders(true, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const modifyIngrediant = createAsyncThunk(
  "ingrediant/modifyIngrediant",
  async ({ body, ingrediantId, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/ingrediant/update/${ingrediantId}`,
        body,
        getAuthHeaders(true, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const deleteIngrediant = createAsyncThunk(
  "ingrediant/deleteIngrediant",
  async ({ ingrediantId, restaurantId }) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/ingrediant/${ingrediantId}`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);
const ingrediantSlice = createSlice({
  name: "ingrediant",
  initialState,
  reducers: {
    updateStatus: (state) => {
      state.status = "idle";
    },
    setIngrediantsQuery: (state, action) => {
      state.query = { ...state.query, ...action.payload };
    },
    resetIngrediantsQuery: (state) => {
      state.query = { search: "", limit: 12, page: 1 };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getIngrediants.pending, (state, action) => {
        state.status = "loadingFetchData";
        state.loading = true;
      })
      .addCase(getIngrediants.fulfilled, (state, action) => {
        state.status = "fetchData";
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(getIngrediants.rejected, (state, action) => {
        state.status = "fetchError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(getIngrediantsPaginated.pending, (state, action) => {
        state.status = "loadingFetchDataPaginated";
        state.loading = true;
      })
      .addCase(getIngrediantsPaginated.fulfilled, (state, action) => {
        state.status = "fetchDataPaginated";
        state.loading = false;
        state.items = action.payload.ingrediants || [];
        state.pagination = action.payload.pagination;
        state.lastParams = JSON.stringify(action.meta.arg);
      })
      .addCase(getIngrediantsPaginated.rejected, (state, action) => {
        state.status = "fetchErrorPaginated";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addIngrediant.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(addIngrediant.fulfilled, (state, action) => {
        state.status = "addSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(addIngrediant.rejected, (state, action) => {
        state.status = "addError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteIngrediant.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(deleteIngrediant.fulfilled, (state, action) => {
        state.status = "deleteSuccess";
        state.loading = false;
        state.success = action.payload.message;

        const deletedId = action.meta.arg.ingrediantId;
        state.items = state.items.filter(item => item._id !== deletedId);
      })
      .addCase(deleteIngrediant.rejected, (state, action) => {
        state.status = "deleteError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(modifyIngrediant.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(modifyIngrediant.fulfilled, (state, action) => {
        state.status = "modifySuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(modifyIngrediant.rejected, (state, action) => {
        state.status = "modifyError";
        state.loading = false;
        state.error = action.error.message;
      });
  },
});
export const { updateStatus, setIngrediantsQuery, resetIngrediantsQuery } =
  ingrediantSlice.actions;
export const selectAllIngrediants = (state) => state.ingrediant.items;
export const getIngrediantsStatus = (state) => state.ingrediant.status;
export const getIngrediantsError = (state) => state.ingrediant.error;
export const getIngrediantsSuccess = (state) => state.ingrediant.success;
export const getIngrediantsLoading = (state) => state.ingrediant.loading;
export const selectIngrediantsPagination = (state) =>
  state.ingrediant.pagination;
export const selectIngrediantsLastParams = (state) =>
  state.ingrediant.lastParams;
export const selectIngrediantsQuery = (state) => state.ingrediant.query;
export default ingrediantSlice.reducer;
